import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Table, Badge, Pagination, Button } from 'react-bootstrap';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import {
  getTableHeaderClass,
  getModernDataTableClass,
  getModernCardHeaderClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

const Results = ({ data, permission, currentTheme }) => {
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState(''); // Nova state para coluna de ordenação
  const [orderDirection, setOrderDirection] = useState('asc'); // Nova state para direção da ordenação

  // Função para manipular o clique no cabeçalho e ordenar
  const handleSort = (column) => {
    if (orderBy === column) {
      // Inverte a direção se a coluna já está selecionada
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Define uma nova coluna e inicia com ascendente
      setOrderBy(column);
      setOrderDirection('asc');
    }
    // Resetar para primeira página quando mudar ordenação
    setPage(0);
  };

  // Função para ordenar os dados com base na coluna e direção
  const sortData = (dataToSort) => {
    if (!orderBy) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      let valueA, valueB;

      // Definir valores para comparação com base na coluna
      switch (orderBy) {
        case 'serialnumber':
          valueA = a?.serialnumber || '';
          valueB = b?.serialnumber || '';
          break;
        case 'status':
          valueA = a?.status?.error || '';
          valueB = b?.status?.error || '';
          break;
        case 'date':
          // Convertendo as datas para timestamp
          valueA = a?.status?.date ? new Date(a.status.date).getTime() : 0;
          valueB = b?.status?.date ? new Date(b.status.date).getTime() : 0;
          break;
        case 'company':
          valueA = a?.company?.name || '';
          valueB = b?.company?.name || '';
          break;
        case 'local':
          valueA = a?.nameEquipament || '';
          valueB = b?.nameEquipament || '';
          break;
        case 'software':
          valueA = a?.versaosw || '';
          valueB = b?.versaosw || '';
          break;
        default:
          valueA = '';
          valueB = '';
      }

      // Comparação dos valores considerando a direção
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return orderDirection === 'asc' ? valueA - valueB : valueB - valueA;
      } else {
        const comparison = String(valueA).localeCompare(String(valueB));
        return orderDirection === 'asc' ? comparison : -comparison;
      }
    });
  };

  // Renderizar ícone de ordenação usando react-icons
  const renderSortIcon = (column) => {
    if (orderBy !== column) {
      return <FaSort style={{ marginLeft: 5, opacity: 0.5 }} />;
    }
    return orderDirection === 'asc'
      ? <FaSortUp style={{ marginLeft: 5, opacity: 1 }} />
      : <FaSortDown style={{ marginLeft: 5, opacity: 1 }} />;
  };

  // Aplicar ordenação antes da paginação
  const sortedData = sortData(data);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0); // Reset to first page
  };

  const handleClick = (serial) => {
    history.push(`/supportdry/${serial}`);
  };

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    let range = [];

    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const getVariant = (erro) => {
    switch (erro) {
      case "Crítico":
        return "danger";
      case "Moderado":
        return "warning";
      case "Baixo":
        return "info";
      case "Sucesso":
        return "success";
      case "Secondary":
        return "secondary";
      default:
        return "primary";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    // Diferença em milissegundos e conversão para horas
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const isOld = diffInHours > 12;

    // Formatando a data corretamente sem converter fuso horário
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = String(date.getUTCFullYear()).slice(-2);
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");

    return {
      formattedDate: `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`,
      isOld,
    };
  };

  return (
    <div>
      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages}
      </p>
      <Badge className='p-1 me-2 mb-2' bg={"success"}>{"Sucesso"}</Badge>
      <Badge className='p-1 me-2 mb-2' bg={"secondary"}>{"Normal"}</Badge>
      <Badge className='p-1 me-2 mb-2' bg={"info"}>{"Baixo"}</Badge>
      <Badge className='p-1 me-2 mb-2' bg={"warning"}>{"Moderado"}</Badge>
      <Badge className='p-1 me-2 mb-2' bg={"danger"}>{"Crítico"}</Badge>
      <Card
        className={getModernDataTableClass(currentTheme)}
      >
        <Card.Header
          className={getModernCardHeaderClass(currentTheme)}
        >
          Equipamentos
        </Card.Header>
        <Card.Body
          className={getModernCardBodyClass(currentTheme)}
        >
          <Table
            style={{ fontSize: "0.9rem" }}
            hover
            responsive
            size="sm"
            className={getModernTableClass(currentTheme)}
          >
            <thead>
              <tr className={getTableHeaderClass(currentTheme)}>
                <th
                  className="text-center"
                  onClick={() => handleSort('serialnumber')}
                  style={{ cursor: 'pointer' }}
                >
                  SERIAL {renderSortIcon('serialnumber')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('status')}
                  style={{ cursor: 'pointer' }}
                >
                  STATUS {renderSortIcon('status')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('date')}
                  style={{ cursor: 'pointer' }}
                >
                  HORA {renderSortIcon('date')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('company')}
                  style={{ cursor: 'pointer' }}
                >
                  EMPRESA {renderSortIcon('company')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('local')}
                  style={{ cursor: 'pointer' }}
                >
                  LOCAL {renderSortIcon('local')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('software')}
                  style={{ cursor: 'pointer' }}
                >
                  SOFTWARE {renderSortIcon('software')}
                </th>
                <th className="text-center">AÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item) => (
                <tr key={item._id}>
                  <td className="text-center pt-2 pb-2">
                    <Badge className='p-1' bg={item?.status?.tipo ? getVariant(item?.status?.tipo) : "secondary"}>{item?.serialnumber}</Badge>
                  </td>

                  <td className="text-center pt-2 pb-2" title={item?.status?.error || ""}>
                    {item?.status?.error
                      ? item?.status?.error?.length < 50
                        ? item?.status?.error
                        : `${item?.status?.error?.slice(0, 50)}...`
                      : 'Sem status'}
                  </td>

                  {(() => {
                    if (item?.status?.date) {
                      const { formattedDate, isOld } = formatDate(item.status.date);
                      return <td className={`text-center pt-2 pb-2 ${isOld ? 'text-danger' : ''}`}>{formattedDate}</td>;
                    }
                    return <td className="text-center pt-2 pb-2">--</td>;
                  })()}

                  <td className="text-center pt-2 pb-2">
                    {item?.company?.name
                      ? item.company?.name?.length < 40
                        ? item.company?.name
                        : `${item.company?.name?.slice(0, 40)}...`
                      : '--'}
                  </td>

                  <td className="text-center pt-2 pb-2">{item?.nameEquipament || '--'}</td>
                  <td className="text-center pt-2 pb-2">{item?.versaosw || '--'}</td>
                  <td className="text-center">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick(item.serialnumber)
                      }}
                      disabled={!permission?.write}
                    >
                      Conectar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer
          className={getModernCardFooterClass(currentTheme)}
        >
          <div>
            <label>
              Linhas por página:{' '}
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="form-select form-select-sm"
              >
                {[20, 40, 60].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Pagination className="custom-pagination" variant="dark">
            <Pagination.Prev
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 0}
              aria-label="Página anterior"
            />
            {getPaginationRange().map((index) => (
              <Pagination.Item
                key={index}
                active={index === page}
                onClick={() => handlePageChange(index)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Próxima página"

            />
          </Pagination>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Results;
